/**
 * AI Summary Service
 * 
 * Provides AI-powered summary generation by calling backend API endpoints.
 * Falls back to rule-based generation if backend is unavailable or returns errors.
 * 
 * Backend Endpoint: POST /api/app/ai/generateSummary
 * 
 * Usage:
 * ```typescript
 * import { AISummaryService } from './aiSummaryService';
 * 
 * // Generate summary for a request
 * const summary = await AISummaryService.generateRequestSummary({
 *   appointmentType: 'On-Site Estimate',
 *   customerName: 'John Doe',
 *   answers: [...],
 *   notes: 'Customer called...',
 *   preferredDate: '2025-11-12'
 * });
 * ```
 */

import { RequestSummaryService, RequestSummaryData } from './requestSummaryService';
import { request } from '../api/core/request';
import { OpenAPI } from '../api/core/OpenAPI';
import { parseBackendDate } from '../utils/timeUtils';
import {
  isAppointmentBriefingAgentAvailable,
  generateAppointmentBriefingWithClaude,
} from '../business/routy/appointmentBriefingAgent';
import { recordRoutyUsage } from '../business/routy/routyUsageTracker';

/**
 * Request body for backend AI summary generation
 */
export interface GenerateSummaryRequest {
  entityType: 'Request' | 'Appointment' | 'Booking';
  entityId?: string;
  data: RequestSummaryData | AppointmentSummaryData | BookingSummaryData;
  /** Appointment briefings only: 'summary' skips the sales-opportunity
   * analysis for a faster, cheaper generation; 'salesPrep' (default) runs
   * the full analysis including sales coaching. */
  mode?: AppointmentBriefingMode;
}

/**
 * What kind of appointment briefing to generate:
 * - 'summary'   — plain summary + before-arrival only. No sales analysis, so
 *                 it's significantly faster and cheaper.
 * - 'salesPrep' — the full briefing: summary + before-arrival + sales
 *                 coaching (when the meeting is genuinely a sales opportunity).
 */
export type AppointmentBriefingMode = 'summary' | 'salesPrep';

/**
 * Public-record property data pulled for the appointment's address (square
 * footage, beds/baths, year built, last sold price, etc.) so estimators see
 * the facts they'd otherwise look up by hand. Produced by a web-search
 * lookup (see `business/routy/propertyRecordsAgent.ts`); every field is
 * optional because only values actually found in records are reported —
 * nothing is estimated. `found: false` means the search ran but no records
 * matched this address, which the UI surfaces as an explicit "no records
 * found" state rather than silently omitting the section.
 */
export interface PropertyRecords {
  found: boolean;
  propertyType?: string;
  squareFeet?: number;
  bedrooms?: number;
  bathrooms?: number;
  yearBuilt?: number;
  lotSize?: string;
  lastSoldPrice?: string;
  lastSoldDate?: string;
  estimatedValue?: string;
  additionalNotes?: string;
  /** Where the data came from and its reliability — always shown so nobody
   * mistakes aggregator data for a verified county record. */
  sourceNote?: string;
}

/** A single labeled talking point, e.g. `{ title: "Bundle the punch list", detail: "..." }` — rendered as
 * "**Bundle the punch list.** ..." so the reason/action is scannable before the explanation. */
export interface AppointmentInsightPoint {
  title: string;
  detail: string;
}

/** A themed group of on-site questions, e.g. `{ category: "Clarify the situation", questions: [...] }`. */
export interface AppointmentQuestionGroup {
  category: string;
  questions: string[];
}

/**
 * Sales-coaching insight for an appointment. Only meaningful — and only ever
 * rendered — when `isSalesOpportunity` is true. The backend/Claude should
 * only set this when the appointment is clearly a sales/estimate-type
 * meeting; for anything ambiguous (ad-hoc stops, service calls, unclear
 * intent) it should be omitted entirely so the UI just shows the plain
 * summary instead of guessing at a sales angle that doesn't apply.
 */
export interface AppointmentMeetingInsight {
  isSalesOpportunity: boolean;
  /** Short label describing what kind of sales situation this is, e.g.
   * "Multi-scope estimate tied to a real estate closing — competitive shopping situation." */
  meetingTypeLabel?: string;
  /** One or two sentences of context on why it's that kind of situation. */
  meetingTypeDescription?: string;
  /** Strategic angles for winning the job — not generic sales tips, but
   * specific to what this appointment's context implies. */
  salesAngle?: AppointmentInsightPoint[];
  /** Questions to ask on-site, grouped by purpose (clarify, uncover criteria,
   * build the bundle case, create urgency, close). */
  leadingQuestions?: AppointmentQuestionGroup[];
}

/**
 * Response from backend AI summary generation
 */
export interface GenerateSummaryResponse {
  summary: string;
  fullSummary?: string;
  /** Pre-split talking points. If the backend doesn't provide these, the
   * client derives them from `fullSummary`/`summary`. */
  keyPoints?: string[];
  /** Before-arrival instructions (call-ahead requests, pet/safety warnings,
   * gate codes, items to bring) pulled out of the general summary so they
   * render as their own highlighted section (QA 86bav5z99). Omitted when
   * nothing applies. */
  beforeArrival?: string[];
  /** Customer-context notes for the sales conversation (buying stage,
   * timeline, comparison shopping, decision criteria) grouped out of the
   * general summary (QA 86bav6122). Omitted when nothing applies. */
  salesNotes?: string[];
  /** Public-record property data for the appointment address (sq ft, beds/
   * baths, year built, last sold). Omitted when the lookup didn't run or
   * failed; `found: false` when it ran and nothing matched. */
  propertyRecords?: PropertyRecords;
  /** Present only for appointments; omitted (or `isSalesOpportunity: false`)
   * when the meeting type isn't clearly a sales/estimate situation. */
  meetingInsight?: AppointmentMeetingInsight;
  provider: 'OpenAI' | 'Claude' | 'Fallback' | 'Cache';
  cached: boolean;
  generatedAt: string;
}

/** A single booking-form question/answer pair, used to give the AI (and the
 * rule-based fallback) the full context of what the customer told us. */
export interface AppointmentFormAnswer {
  question: string;
  answer: string;
  /** True when this answer matched a "request-only" trigger option — i.e.
   * something that should have routed to manual review (special equipment,
   * hazards, etc.) and is worth calling out to the person going on-site. */
  flagged?: boolean;
}

/** A staff note on the appointment or its request — e.g. an ad-hoc internal
 * note a rep left ("back patio", "call before arriving"). There can be
 * several of these, each optionally titled. */
export interface AppointmentInternalNote {
  title?: string;
  content: string;
}

/**
 * Data structure for appointment summaries.
 *
 * This is sent as-is to Claude (via the backend) so it can write a short,
 * plain-English briefing. Callers should pre-format any date/time fields
 * with the shared `timeUtils` helpers before populating `*Display` fields —
 * this service intentionally does no date parsing of its own.
 */
export interface AppointmentSummaryData {
  serviceType?: string;
  customerName?: string;
  businessName?: string;
  address?: string;
  /** Raw ISO/backend timestamp so the LLM can reason about "today"/"in 2 hours" etc. */
  scheduledDateTime?: string;
  /** Human-readable, pre-formatted version of `scheduledDateTime` for display/fallback. */
  scheduledDateTimeDisplay?: string;
  durationMinutes?: number;
  assignedUser?: string;
  status?: string;
  /** Staff notes on the appointment itself (the notes bundle's `appointment` list). */
  internalNotes?: AppointmentInternalNote[];
  /** Staff notes left on the request before it was booked (the bundle's `preBooking` list). */
  preBookingNotes?: AppointmentInternalNote[];
  /** The customer's own note(s) on the request (the bundle's `customer` list, joined), e.g. "gate code is 1234". */
  customerNote?: string;
  /** The customer's own free-text description of the work needed, from the
   * request (distinct from the structured booking-form Q&A in `formResponses`). */
  projectDetail?: string;
  /** Why the appointment was cancelled, if it was. */
  cancellationReason?: string;
  /** Where the lead came from (Google, Referral, Facebook, etc.), display-formatted. */
  leadSource?: string;
  /** How the booking was created — Web, Chatbot, Internal, VoiceBot. */
  origin?: string;
  isAsap?: boolean;
  /** Pre-formatted display string for when the booking was created. */
  bookingCreatedAtDisplay?: string;
  bookingCreatedBy?: string;
  /** Full set of booking-form question/answer pairs the customer submitted. */
  formResponses?: AppointmentFormAnswer[];
  hasAccessCode?: boolean;
  hasPhotos?: boolean;
}

/**
 * A ready-to-render AI briefing: a one-line headline plus a short list of
 * key points worth knowing before heading to the appointment, and — only
 * when this is recognizably a sales/estimate meeting — a sales-coaching
 * `insight` block with the angle to take and questions to ask on-site.
 */
export interface AppointmentBriefing {
  headline: string;
  keyPoints: string[];
  /** Access/safety/prep instructions to act on BEFORE arriving (call ahead,
   * dog warning, gate code, items to bring) — rendered as a highlighted
   * "Before Arrival" section so they can't be missed among the key points. */
  beforeArrival?: string[];
  /** Customer-context notes for the sales conversation (buying stage,
   * timeline, comparison shopping) — rendered as a "Sales Notes" section
   * instead of being mixed into the key points. */
  salesNotes?: string[];
  /** Public-record property data for the address — rendered as a "Property
   * Records" section in both the summary and sales-prep views. Undefined
   * when the lookup didn't run or failed; `found: false` when it ran and
   * found nothing (rendered as an explicit no-records state). */
  property?: PropertyRecords;
  insight?: AppointmentMeetingInsight;
  provider: GenerateSummaryResponse['provider'];
  cached: boolean;
}

/**
 * Data structure for booking summaries
 */
export interface BookingSummaryData {
  bookingType?: string;
  customerName?: string;
  answers?: Array<{ question: string; answer: string; }>;
  specialRequests?: string;
}

/**
 * AI Summary Service
 * 
 * Calls backend API for AI-powered summaries with automatic fallback to rule-based generation
 */
export class AISummaryService {
  /**
   * Generate AI-powered summary for a request
   * 
   * @param data Request data to summarize
   * @param entityId Optional entity ID for caching
   * @param maxLength Maximum length for card display (default: 150)
   * @returns AI-generated summary text
   */
  static async generateRequestSummary(
    data: RequestSummaryData,
    entityId?: string,
    maxLength: number = 150
  ): Promise<string> {
    const startedAt = Date.now();
    try {
      // Call backend API
      const response = await this.callBackendAPI('Request', data, entityId);
      recordRoutyUsage({
        surface: 'request_summary',
        outcome: 'ok',
        provider: response.provider,
        latencyMs: Date.now() - startedAt,
      });

      // Truncate if needed for card display
      if (response.summary.length > maxLength) {
        return this.truncateAtSentence(response.summary, maxLength);
      }
      
      return response.summary;
    } catch (error) {
      console.warn('AI summary generation failed, using fallback:', error);
      recordRoutyUsage({ surface: 'request_summary', outcome: 'fallback', provider: 'Fallback' });
      
      // Fallback to rule-based generation
      return RequestSummaryService.generateCardSummary(data, maxLength);
    }
  }

  /**
   * Generate AI-powered summary for an appointment
   * 
   * @param data Appointment data to summarize
   * @param entityId Optional entity ID for caching
   * @param maxLength Maximum length for card display (default: 150)
   * @returns AI-generated summary text
   */
  static async generateAppointmentSummary(
    data: AppointmentSummaryData,
    entityId?: string,
    maxLength: number = 150
  ): Promise<string> {
    const startedAt = Date.now();
    try {
      // Call backend API
      const response = await this.callBackendAPI('Appointment', data, entityId);
      recordRoutyUsage({
        surface: 'appointment_summary',
        outcome: 'ok',
        provider: response.provider,
        latencyMs: Date.now() - startedAt,
        isSalesOpportunity: !!response.meetingInsight?.isSalesOpportunity,
      });

      // Truncate if needed for card display
      if (response.summary.length > maxLength) {
        return this.truncateAtSentence(response.summary, maxLength);
      }
      
      return response.summary;
    } catch (error) {
      console.warn('AI appointment summary generation failed, using fallback:', error);
      recordRoutyUsage({ surface: 'appointment_summary', outcome: 'fallback', provider: 'Fallback' });
      
      // Fallback to basic summary
      return this.generateAppointmentFallback(data, maxLength);
    }
  }

  /**
   * Generate AI-powered summary for a booking
   * 
   * @param data Booking data to summarize
   * @param entityId Optional entity ID for caching
   * @param maxLength Maximum length for card display (default: 150)
   * @returns AI-generated summary text
   */
  static async generateBookingSummary(
    data: BookingSummaryData,
    entityId?: string,
    maxLength: number = 150
  ): Promise<string> {
    const startedAt = Date.now();
    try {
      // Call backend API
      const response = await this.callBackendAPI('Booking', data, entityId);
      recordRoutyUsage({
        surface: 'booking_summary',
        outcome: 'ok',
        provider: response.provider,
        latencyMs: Date.now() - startedAt,
      });

      // Truncate if needed for card display
      if (response.summary.length > maxLength) {
        return this.truncateAtSentence(response.summary, maxLength);
      }
      
      return response.summary;
    } catch (error) {
      console.warn('AI booking summary generation failed, using fallback:', error);
      recordRoutyUsage({ surface: 'booking_summary', outcome: 'fallback', provider: 'Fallback' });
      
      // Fallback to basic summary
      return this.generateBookingFallback(data, maxLength);
    }
  }

  /**
   * Generate a Routy AI briefing for an appointment: a short headline plus a
   * handful of key points (lead source, notable form answers, booking time,
   * customer notes, etc.) that matter to whoever is heading to the appointment.
   *
   * When the backend recognizes this as a sales/estimate-type meeting, the
   * response also includes an `insight` block — the sales angle to take and
   * questions to ask on-site — built from the specific context of this
   * appointment (not generic sales tips). For anything else (service calls,
   * ad-hoc stops, or meetings where the intent isn't clear), `insight` is
   * omitted and only the plain summary is shown.
   *
   * Calls the backend (which pings Claude) and gracefully degrades to a
   * rule-based briefing built from the same data if the backend is
   * unavailable. The fallback intentionally never fabricates sales coaching
   * — that requires real reasoning about the situation — so it only ever
   * produces the plain summary.
   *
   * @param data Rich appointment context to summarize
   * @param entityId Appointment id, used for backend-side caching and to key
   * the client-side cache used by the browser Claude prototype path below
   * @param forceRefresh Skip the client-side cache (e.g. the card's manual
   * refresh button) and generate a fresh briefing
   * @param mode 'summary' for the fast plain-summary-only briefing;
   * 'salesPrep' (default) for the full briefing including sales coaching
   */
  static async generateAppointmentBriefing(
    data: AppointmentSummaryData,
    entityId?: string,
    forceRefresh: boolean = false,
    mode: AppointmentBriefingMode = 'salesPrep'
  ): Promise<AppointmentBriefing> {
    const startedAt = Date.now();
    try {
      const response = await this.callBackendAPI('Appointment', data, entityId, mode);
      const keyPoints = response.keyPoints && response.keyPoints.length > 0
        ? response.keyPoints
        : this.splitIntoKeyPoints(response.fullSummary || response.summary);

      recordRoutyUsage({
        surface: 'appointment_summary',
        outcome: 'ok',
        provider: response.provider,
        latencyMs: Date.now() - startedAt,
        isSalesOpportunity: !!response.meetingInsight?.isSalesOpportunity,
        mode,
      });
      this.recordGeneratedBriefing(entityId, mode, data, parseBackendDate(response.generatedAt));
      return {
        headline: response.summary || 'Appointment summary',
        keyPoints: keyPoints.length > 0 ? keyPoints : ['No additional details provided for this appointment.'],
        beforeArrival: response.beforeArrival?.length ? response.beforeArrival : undefined,
        salesNotes: response.salesNotes?.length ? response.salesNotes : undefined,
        property: response.propertyRecords,
        insight: response.meetingInsight?.isSalesOpportunity ? response.meetingInsight : undefined,
        provider: response.provider,
        cached: response.cached,
      };
    } catch (backendError) {
      // The backend endpoint (docs/implementation/appointment-ai-summary-backend-spec.md)
      // isn't live yet in most environments. While that's the case, fall back to
      // calling Claude through the backend Anthropic proxy — the same routing
      // RoutyAgent uses (no browser key needed) — so the feature (including
      // meeting-type/sales-angle recognition) works wherever Routy is enabled.
      // Once the backend ships, the try above succeeds and this path is never
      // reached.
      if (isAppointmentBriefingAgentAvailable()) {
        const cacheKey = `${entityId || 'unknown'}:${mode}:${this.hashBriefingInput(data)}`;
        // A cached full (sales-prep) briefing already contains everything a
        // summary-only request needs, so serve it for either mode.
        const cached = !forceRefresh
          ? (this.appointmentBriefingCache.get(cacheKey)
            ?? (mode === 'summary'
              ? this.appointmentBriefingCache.get(`${entityId || 'unknown'}:salesPrep:${this.hashBriefingInput(data)}`)
              : undefined))
          : undefined;
        if (cached) {
          // Carry the sales flag (and mode) on cache hits too — the admin
          // aggregates use provider to separate fresh generations from
          // re-views, so dropping the flag here would just lose data.
          recordRoutyUsage({
            surface: 'appointment_summary',
            outcome: 'ok',
            provider: 'Cache',
            isSalesOpportunity: !!cached.insight?.isSalesOpportunity,
            mode,
          });
          this.recordGeneratedBriefing(entityId, mode, data);
          return { ...cached, provider: 'Cache', cached: true };
        }

        try {
          const briefing = await generateAppointmentBriefingWithClaude(data, mode);
          this.appointmentBriefingCache.set(cacheKey, briefing);
          recordRoutyUsage({
            surface: 'appointment_summary',
            outcome: 'ok',
            provider: 'Claude',
            latencyMs: Date.now() - startedAt,
            isSalesOpportunity: !!briefing.insight?.isSalesOpportunity,
            mode,
          });
          this.recordGeneratedBriefing(entityId, mode, data);
          return briefing;
        } catch (claudeError) {
          console.warn('Routy AI (browser Claude prototype) briefing failed, using rule-based summary:', claudeError);
        }
      } else {
        console.warn('Routy AI appointment briefing failed, using rule-based summary:', backendError);
      }

      recordRoutyUsage({ surface: 'appointment_summary', outcome: 'fallback', provider: 'Fallback', mode });
      return {
        ...this.generateAppointmentBriefingFallback(data),
        provider: 'Fallback',
        cached: false,
      };
    }
  }

  /**
   * In-memory cache for the browser Claude prototype path only — avoids
   * re-calling Anthropic every time the same unmodified appointment panel is
   * reopened in a session. Keyed by entityId + a hash of the input, so any
   * change to the underlying data (new answers, edited notes, etc.)
   * naturally produces a fresh generation. Cleared on full page reload; the
   * real backend endpoint should own durable/cross-session caching.
   *
   * @private
   */
  private static appointmentBriefingCache = new Map<string, AppointmentBriefing>();

  /**
   * What was successfully generated for each appointment this session, per
   * mode (regardless of which path produced it - backend endpoint or the
   * browser Claude prototype). Beyond the "was it generated" flag (QA
   * 86bav636q's ready state), each record keeps the INPUT HASH and note
   * count it was generated from, so the UI can detect that the appointment's
   * data (e.g. a new note) changed after generation and offer an update
   * (QA 86bav66q4). Rule-based fallbacks are NOT recorded: nothing was
   * cached for them, so presenting a "ready" state would be misleading.
   *
   * @private
   */
  private static generatedBriefings = new Map<string, Map<AppointmentBriefingMode, {
    inputHash: string;
    generatedAt: Date;
    noteCount: number;
  }>>();

  /** All note-ish inputs a user would call "a note" when they add one. @private */
  private static countBriefingNotes(data: AppointmentSummaryData): number {
    return (data.internalNotes?.length || 0) + (data.preBookingNotes?.length || 0) + (data.customerNote ? 1 : 0);
  }

  /** @private */
  private static recordGeneratedBriefing(
    entityId: string | undefined,
    mode: AppointmentBriefingMode,
    data: AppointmentSummaryData,
    generatedAt?: Date | null,
  ): void {
    const key = entityId || 'unknown';
    const inputHash = this.hashBriefingInput(data);
    const perMode = this.generatedBriefings.get(key) ?? new Map();
    // Identical input = identical content (it came from a cache), so keep the
    // ORIGINAL generation time from whichever mode's record carries it.
    const sameInput = [...perMode.values()].find((r) => r.inputHash === inputHash);
    perMode.set(mode, {
      inputHash,
      generatedAt: generatedAt ?? sameInput?.generatedAt ?? new Date(),
      noteCount: this.countBriefingNotes(data),
    });
    this.generatedBriefings.set(key, perMode);
  }

  /**
   * Whether a briefing for this appointment was already generated this
   * session (per mode). Lets the card present a "ready / view" state instead
   * of a "generate" action after the panel is closed and reopened, so users
   * don't think a fresh (credit-burning) generation is needed (QA 86bav636q).
   * Tapping view still runs the real pipeline, which serves the cache when
   * the data is unchanged and quietly regenerates when it isn't.
   */
  static hasGeneratedAppointmentBriefing(entityId?: string): { summary: boolean; salesPrep: boolean; any: boolean } {
    const perMode = this.generatedBriefings.get(entityId || 'unknown');
    const summary = !!perMode?.has('summary');
    const salesPrep = !!perMode?.has('salesPrep');
    return { summary, salesPrep, any: summary || salesPrep };
  }

  /**
   * Compare the appointment's CURRENT briefing input against what the last
   * generated briefing was built from (QA 86bav66q4). `stale` means the data
   * changed since generation — the existing summary may no longer reflect
   * the appointment. `newNoteCount` > 0 pinpoints the most common cause (a
   * note added after generation) so the UI can say "N new notes were added"
   * instead of a vague "something changed". `generatedAt` supports a "last
   * generated X ago" label. Pass `mode` for the briefing being displayed;
   * omit it to check whichever mode was generated (salesPrep preferred).
   */
  static getAppointmentBriefingStatus(
    entityId: string | undefined,
    currentData: AppointmentSummaryData,
    mode?: AppointmentBriefingMode,
  ): { generated: boolean; generatedAt: Date | null; stale: boolean; newNoteCount: number } {
    const perMode = this.generatedBriefings.get(entityId || 'unknown');
    const record = mode ? perMode?.get(mode) : perMode?.get('salesPrep') ?? perMode?.get('summary');
    if (!record) return { generated: false, generatedAt: null, stale: false, newNoteCount: 0 };
    const stale = this.hashBriefingInput(currentData) !== record.inputHash;
    return {
      generated: true,
      generatedAt: record.generatedAt,
      stale,
      newNoteCount: stale ? Math.max(0, this.countBriefingNotes(currentData) - record.noteCount) : 0,
    };
  }

  /**
   * Cheap, deterministic hash of the briefing input for cache-keying —
   * doesn't need to be cryptographic, just stable for identical inputs.
   *
   * @private
   */
  private static hashBriefingInput(data: AppointmentSummaryData): string {
    const serialized = JSON.stringify(data);
    let hash = 0;
    for (let i = 0; i < serialized.length; i++) {
      hash = (hash * 31 + serialized.charCodeAt(i)) | 0;
    }
    return hash.toString(36);
  }

  /**
   * Break a paragraph-style AI summary into short standalone bullet points
   * for card display, when the backend doesn't already return `keyPoints`.
   *
   * @private
   */
  private static splitIntoKeyPoints(text?: string): string[] {
    if (!text) return [];
    return text
      .split(/\r?\n+|(?<=[.!?])\s+(?=[A-Z•\-\d])/)
      .map(s => s.replace(/^[-•\s]+/, '').trim())
      .filter(Boolean)
      .slice(0, 6);
  }

  /**
   * Rule-based appointment briefing used when Claude/the backend is
   * unavailable. Surfaces the same fields a human dispatcher would scan for:
   * lead source, how/when it was booked, notable Q&A, notes, and anything
   * flagged for manual attention.
   *
   * @private
   */
  private static generateAppointmentBriefingFallback(
    data: AppointmentSummaryData
  ): { headline: string; keyPoints: string[]; beforeArrival?: string[] } {
    const headlineParts: string[] = [];
    if (data.serviceType) headlineParts.push(data.serviceType);
    if (data.customerName) headlineParts.push(`with ${data.customerName}`);
    else if (data.businessName) headlineParts.push(`with ${data.businessName}`);
    if (data.scheduledDateTimeDisplay) headlineParts.push(`on ${data.scheduledDateTimeDisplay}`);
    const headline = headlineParts.length > 0
      ? headlineParts.join(' ')
      : 'Upcoming appointment';

    const keyPoints: string[] = [];

    if (data.isAsap) {
      keyPoints.push('Booked as ASAP — the customer requested the soonest available time.');
    }

    if (data.leadSource) {
      keyPoints.push(`Lead source: ${data.leadSource}${data.origin ? ` via ${data.origin}` : ''}.`);
    } else if (data.origin) {
      keyPoints.push(`Booked via ${data.origin}.`);
    }

    if (data.bookingCreatedAtDisplay) {
      keyPoints.push(
        `Booked ${data.bookingCreatedAtDisplay}${data.bookingCreatedBy ? ` by ${data.bookingCreatedBy}` : ''}.`
      );
    }

    if (data.projectDetail) {
      keyPoints.push(`Project details: ${this.truncateAtSentence(data.projectDetail, 160)}`);
    }

    // Rule-based "Before Arrival" section: without real reasoning we can only
    // hoist the unambiguous access/safety signals (flagged answers and the
    // access-code flag) - the Claude path extracts these from free text too.
    const beforeArrival: string[] = [];

    const answers = data.formResponses || [];
    const flagged = answers.filter(a => a.flagged);
    const rest = answers.filter(a => !a.flagged);

    flagged.slice(0, 3).forEach(a => {
      beforeArrival.push(`${a.question}: ${this.truncateAtSentence(a.answer, 140)}`);
    });
    rest.slice(0, 3).forEach(a => {
      keyPoints.push(`${a.question}: ${this.truncateAtSentence(a.answer, 140)}`);
    });

    if (data.customerNote) {
      keyPoints.push(`Customer note: "${this.truncateAtSentence(data.customerNote, 140)}"`);
    }
    [...(data.preBookingNotes || []), ...(data.internalNotes || [])].slice(0, 3).forEach(note => {
      const label = note.title ? note.title : 'Internal note';
      keyPoints.push(`${label}: "${this.truncateAtSentence(note.content, 140)}"`);
    });
    if (data.hasAccessCode) {
      beforeArrival.push('Property has a gate/access code on file — check the appointment for details before arriving.');
    }
    if (data.hasPhotos) {
      keyPoints.push('Customer attached photos to this request — worth a look before heading out.');
    }
    if (data.durationMinutes) {
      keyPoints.push(`Expect this to take about ${data.durationMinutes} minutes.`);
    }
    if (data.cancellationReason) {
      keyPoints.push(`Cancelled: ${this.truncateAtSentence(data.cancellationReason, 140)}`);
    }

    return {
      headline,
      keyPoints: keyPoints.length > 0
        ? keyPoints.slice(0, 8)
        : ['No additional details provided for this appointment.'],
      beforeArrival: beforeArrival.length > 0 ? beforeArrival.slice(0, 5) : undefined,
    };
  }

  /**
   * Call backend AI summary generation API
   * 
   * @private
   */
  private static async callBackendAPI(
    entityType: 'Request' | 'Appointment' | 'Booking',
    data: any,
    entityId?: string,
    mode?: AppointmentBriefingMode
  ): Promise<GenerateSummaryResponse> {
    const requestBody: GenerateSummaryRequest = {
      entityType,
      data,
      ...(entityId && { entityId }),
      ...(mode && { mode })
    };

    const response = await request<GenerateSummaryResponse>(OpenAPI, {
      method: 'POST',
      url: '/api/app/ai/generateSummary',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: 'Bad Request',
        401: 'Unauthorized',
        403: 'Forbidden',
        429: 'Too Many Requests',
        500: 'Server Error',
      },
    });

    return response;
  }

  /**
   * Truncate text at sentence boundary
   * 
   * @private
   */
  private static truncateAtSentence(text: string, maxLength: number): string {
    if (text.length <= maxLength) {
      return text;
    }

    // Try to find a sentence boundary before maxLength
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    let truncated = '';

    for (const sentence of sentences) {
      if ((truncated + sentence).length <= maxLength) {
        truncated += sentence;
      } else {
        break;
      }
    }

    // If we couldn't fit even one sentence, do a hard truncate
    if (truncated.length === 0) {
      truncated = text.substring(0, maxLength - 3) + '...';
    }

    return truncated.trim();
  }

  /**
   * Fallback appointment summary generator
   * 
   * @private
   */
  private static generateAppointmentFallback(
    data: AppointmentSummaryData,
    maxLength: number
  ): string {
    const parts: string[] = [];

    if (data.serviceType) {
      parts.push(data.serviceType);
    }

    if (data.customerName) {
      parts.push(`for ${data.customerName}`);
    }

    if (data.scheduledDateTime) {
      try {
        const date = new Date(data.scheduledDateTime);
        const formatted = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        });
        parts.push(`on ${formatted}`);
      } catch {
        // Skip date if parsing fails
      }
    }

    if (data.address) {
      parts.push(`at ${data.address}`);
    }

    if (data.assignedUser) {
      parts.push(`(${data.assignedUser})`);
    }

    let summary = parts.join(' ');

    if (summary.length > maxLength) {
      summary = summary.substring(0, maxLength - 3) + '...';
    }

    return summary || 'Scheduled appointment';
  }

  /**
   * Fallback booking summary generator
   * 
   * @private
   */
  private static generateBookingFallback(
    data: BookingSummaryData,
    maxLength: number
  ): string {
    const parts: string[] = [];

    if (data.bookingType) {
      parts.push(`${data.bookingType} booking`);
    }

    if (data.customerName) {
      parts.push(`for ${data.customerName}`);
    }

    // Extract key info from answers
    if (data.answers && data.answers.length > 0) {
      const keyAnswers = data.answers
        .filter(a => a.answer && a.answer.trim() !== '' && a.answer.toLowerCase() !== 'n/a')
        .slice(0, 2); // Take first 2 meaningful answers

      keyAnswers.forEach(a => {
        if (a.answer.length < 50) {
          parts.push(a.answer);
        }
      });
    }

    if (data.specialRequests) {
      parts.push(data.specialRequests);
    }

    let summary = parts.join('. ');

    if (summary.length > maxLength) {
      summary = summary.substring(0, maxLength - 3) + '...';
    }

    return summary || 'New booking request';
  }

  /**
   * Check if backend AI summary service is available
   * 
   * @returns Promise<boolean> True if backend is available
   */
  static async isBackendAvailable(): Promise<boolean> {
    try {
      // Try to generate a simple test summary
      await this.callBackendAPI('Request', {
        appointmentType: 'Test',
        notes: 'Connection test'
      });
      return true;
    } catch (error) {
      return false;
    }
  }
}


